import * as React from 'react';
import { omit } from 'rambda';
import { Facet, RefinementType, NumericFilter, Refinements } from '@packages/common';
import { RefinementsContext } from '../RefinementsContext';
import { useRefinements } from './useRefinements';
import { numericFilter$ } from './refinementsEvents';

export const useNumericFilter = (facet: Facet) => {
  const { refinements } = React.useContext(RefinementsContext);
  const { updateNumericFilters } = useRefinements();

  const numericFilters = refinements[RefinementType.NUMERIC_FILTERS];
  const getRefinementValue = () => {
    if (numericFilters[facet.featureMapping]) {
      return numericFilters[facet.featureMapping].value;
    }
    return null;
  };

  const getValue = React.useMemo(
    () =>
      (currentValue: { min: number; max: number }): [number, number] => {
        const value = numericFilters?.[facet.featureMapping]?.value ?? currentValue;
        return [value.min, value.max];
      },
    [facet.featureMapping, numericFilters],
  );

  const update = React.useMemo(
    () =>
      ([min, max]: [number, number]) => {
        const updatedFilter: NumericFilter = {
          ...numericFilters[facet.featureMapping],
          unit: facet.unit,
          seoName: facet.seoName,
          relation: facet.relation,
          value: {
            min,
            max,
          },
        };

        const updatedNumericFilters: Refinements[RefinementType.NUMERIC_FILTERS] = {
          ...numericFilters,
          [facet.featureMapping]: updatedFilter,
        };
        updateNumericFilters(updatedNumericFilters);
        numericFilter$.next({ value: updatedFilter, seoName: facet.seoName });
      },
    [facet, numericFilters, updateNumericFilters],
  );

  const remove = React.useMemo(
    () => () => {
      updateNumericFilters(omit([facet.featureMapping], numericFilters));
      numericFilter$.next({ seoName: facet.seoName });
    },
    [facet, numericFilters, updateNumericFilters],
  );

  return { update, remove, getRefinementValue, getValue };
};
